import { Signal } from 'typed-signals';
import { gridPositionToPxPosition } from '../../utils/gridPositionToPxPosition';
import { isConnectedBlocker } from '../../utils/isConnectedBlocker';
import type { TileModel } from './TileModel';
import { TileColor } from '../../TileColor';
import type { XY } from '../../../../utils/MathUtils';
import MathUtils from '../../../../utils/MathUtils';
import type { BlockerModel, TileStackState } from '../../types';
import { generateId } from '../../../../utils/generateId';

export class TileStackModel {
  public readonly onTilesRemoved = new Signal<() => void>();

  public readonly onTilesAdded = new Signal();

  public readonly id = generateId('ts');

  private readonly tiles: TileModel[] = [];

  public locked = false;

  public blocker: BlockerModel | null = null;

  public underTileBlocker: BlockerModel | null = null;

  constructor(
    public x = 0,
    public y = 0,
  ) {}

  public addTiles(tiles: TileModel | TileModel[]): this {
    const tilesToAdd = Array.isArray(tiles) ? tiles : [tiles];
    this.tiles.push(...tilesToAdd);
    this.onTilesAdded.emit();
    return this;
  }

  public getTiles(): TileModel[] {
    return this.tiles.slice();
  }

  public removeTilesAmount(amount: number): TileModel[] {
    const tiles = this.tiles.splice(Math.max(0, this.tiles.length - amount));
    this.onTilesRemoved.emit();
    return tiles;
  }

  public removeAllTiles(): TileModel[] {
    const tiles = this.tiles.slice();
    this.tiles.length = 0;
    this.onTilesRemoved.emit();
    return tiles;
  }

  public getTileCount(): number {
    return this.tiles.length;
  }

  public hasTiles(): boolean {
    return this.tiles.length > 0;
  }

  public getTopmostTile(offset = 1): TileModel | null {
    if (this.tiles.length < offset) return null;
    return this.tiles[this.tiles.length - offset];
  }

  public getTopmostColor(): TileColor {
    return this.getTopmostTile()?.color ?? TileColor.None;
  }

  public getNextCoveredColor(): TileColor {
    const topColor = this.getTopmostColor();
    for (let i = this.tiles.length - 1; i >= 0; i -= 1) {
      const tile = this.tiles[i];
      if (tile.color !== topColor) return tile.color;
    }
    return TileColor.None;
  }

  public getNextColorCount(): number {
    const colors = this.tiles.map((tile) => tile.color);
    const nextColor = this.getNextCoveredColor();
    if (!colors.includes(nextColor)) return 0;
    return this.getMatchingColorCount(nextColor, colors.lastIndexOf(nextColor));
  }

  /**
   * Retrieves all connected blocker stacks starting from and including the current stack.
   *
   * @param neighborsOnly - If true, only considers direct neighbors of the starting stack.
   * @returns An array of connected `TileStackModel` instances.
   *
   * This method performs a breadth-first search to find all connected blocker stacks.
   * It starts from the current stack and explores all connected stacks recursively.
   * If `neighborsOnly` is true, only direct neighbors of the starting stack are considered.
   */
  public getConnectedBlockerStacks(neighborsOnly = false): TileStackModel[] {
    const connections: TileStackModel[] = [];
    const stacksToCheck: TileStackModel[] = [this];
    const rootBlocker = this.blocker;

    if (!rootBlocker || !isConnectedBlocker(rootBlocker)) return connections;

    while (stacksToCheck.length > 0) {
      const stack = stacksToCheck.shift();
      const stackBlocker = stack?.blocker;
      if (!stack || !stackBlocker) continue;

      const connectedStacks = stack.getConnectedStacks();

      const filteredStacks = connectedStacks.filter((connectedStack) => {
        if (!connectedStack.blocker) return false;
        if (!isConnectedBlocker(stackBlocker)) return false;

        const isConnected = stackBlocker.connection.isConnectedTo(connectedStack);
        const wasProcessed = connections.includes(connectedStack) || stacksToCheck.includes(connectedStack);

        const isConnectedNeighbor =
          this.getConnectedStacks().includes(connectedStack) && rootBlocker.connection.isConnectedTo(connectedStack);

        const neighborCheckPassed = neighborsOnly ? isConnectedNeighbor : true;

        return isConnected && !wasProcessed && neighborCheckPassed;
      });

      filteredStacks.forEach((connectedKnotStack) => {
        stacksToCheck.push(connectedKnotStack);
      });

      connections.push(stack);
    }

    return connections;
  }

  public getMatchingColorCount(color: TileColor, fromIndex?: number): number {
    const matchColor = color ?? this.getTopmostColor();
    const { tiles } = this;
    const lastIndex = tiles.length - 1;
    const initialIndex = MathUtils.clamp(fromIndex ?? lastIndex, 0, lastIndex);

    let count = 0;
    for (let i = initialIndex; i >= 0; i -= 1) {
      const tile = tiles[i];
      // Sentry 6025704208
      // HOW CAN THAT BE THE CASE?
      if (!tile) break;
      if (tile.color !== matchColor) break;
      count += 1;
    }
    return count;
  }

  // TO REFACTOR
  private connectedStacks: TileStackModel[] = [];

  public setConnectedStacks(stacks: TileStackModel[]): this {
    this.connectedStacks = stacks;
    return this;
  }

  public getConnectedStacks(): TileStackModel[] {
    return this.connectedStacks.slice();
  }

  public getMatchingConnections(
    color: TileColor,
    excludeFilter?: (stack: TileStackModel) => boolean,
  ): TileStackModel[] {
    const connectedMatches: TileStackModel[] = [];
    if (color === TileColor.None) return connectedMatches;
    const { connectedStacks } = this;

    for (let i = 0; i < connectedStacks.length; i += 1) {
      const stack = connectedStacks[i];
      if (stack.getTopmostColor() !== color) continue;
      if (excludeFilter?.(stack)) continue;
      connectedMatches.push(stack);
    }
    return connectedMatches;
  }

  public hasMatchingConnections(color: TileColor, excludeLocked = false): boolean {
    return (
      color !== TileColor.None &&
      this.connectedStacks.some((stack) => !(excludeLocked && stack.isBlocked()) && stack.getTopmostColor() === color)
    );
  }

  public isBlocked(): boolean {
    return this.locked || !!this.blocker;
  }

  public isEmpty(): boolean {
    return !this.hasTiles() && !this.blocker;
  }

  public toString(): string {
    return `${this.x}x${this.y}`;
  }

  public exportState(): TileStackState {
    return {
      tiles: this.tiles.map((tile) => tile.color),
      x: this.x,
      y: this.y,
    };
  }

  public setBlocker(blocker: BlockerModel | null): this {
    this.blocker = blocker;
    return this;
  }

  public setUnderTileBlocker(blocker: BlockerModel | null): this {
    this.underTileBlocker = blocker;
    return this;
  }

  public isConnectedToColors(colors: TileColor[], ignoreBlocked = false): boolean {
    return this.connectedStacks.some(
      (stack) => (!ignoreBlocked || !stack.isBlocked()) && colors.includes(stack.getTopmostColor()),
    );
  }

  public getDirectionTo(stack: TileStackModel): XY {
    const position = gridPositionToPxPosition(this.x, this.y);
    const connectedPosition = gridPositionToPxPosition(stack.x, stack.y);

    const direction = {
      x: connectedPosition.x - position.x,
      y: connectedPosition.y - position.y,
    } as XY;

    MathUtils.getUnitVector(direction.x, direction.y, direction);

    return direction;
  }

  public isNeighborOf(stack: TileStackModel): boolean {
    return this.getConnectedStacks().some((connected) => connected === stack);
  }

  public reset(): void {
    this.tiles.length = 0;
    this.locked = false;
    this.blocker = null;
    this.underTileBlocker = null;
  }
}
